import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { Calendar, Trophy, School } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [schools, setSchools] = useState([]);
  const [matches, setMatches] = useState([]);
  const [weeklyLB, setWeeklyLB] = useState({ entries: [], week_start: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');
  const isEN = language === 'en';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lbRes, matchesRes, schoolsRes, weeklyRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/leaderboard`),
          fetch(`${BACKEND_URL}/api/matches?limit=20`),
          fetch(`${BACKEND_URL}/api/leaderboard/schools`),
          fetch(`${BACKEND_URL}/api/leaderboard/weekly`),
        ]);
        setLeaderboard(await lbRes.json());
        setMatches(await matchesRes.json());
        setSchools(await schoolsRes.json());
        if (weeklyRes.ok) setWeeklyLB(await weeklyRes.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  const medalEmoji = (i) => i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1;

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{t('leaderboard', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* Tabs */}
          <div className="lb-tabs" style={{ marginBottom: '20px' }}>
            <button className={`lb-tab ${activeTab === 'weekly' ? 'lb-tab-active' : ''}`}
              onClick={() => setActiveTab('weekly')} data-testid="lb-tab-weekly">
              <Calendar size={14} style={{ marginRight: 4 }} />
              {isEN ? 'This Week' : 'Minggu Ini'}
            </button>
            <button className={`lb-tab ${activeTab === 'schools' ? 'lb-tab-active' : ''}`}
              onClick={() => setActiveTab('schools')} data-testid="lb-tab-schools">
              {t('schoolRanking', language)}
            </button>
            <button className={`lb-tab ${activeTab === 'leaderboard' ? 'lb-tab-active' : ''}`}
              onClick={() => setActiveTab('leaderboard')} data-testid="lb-tab-alltime">
              {t('topTeams', language)}
            </button>
            <button className={`lb-tab ${activeTab === 'matches' ? 'lb-tab-active' : ''}`}
              onClick={() => setActiveTab('matches')} data-testid="lb-tab-matches">
              {t('recentMatches', language)}
            </button>
          </div>

          {loading ? (
            <div className="lb-loading"><div className="lb-spinner" /></div>
          ) : (
            <>
              {/* Weekly Leaderboard */}
              {activeTab === 'weekly' && (
                <div className="lb-table-container" data-testid="leaderboard-weekly-table">
                  <div className="lb-weekly-header">
                    <Trophy size={16} color="#f59e0b" />
                    <span>{isEN ? 'Weekly Champions' : 'Juara Mingguan'}</span>
                    <span className="lb-weekly-reset">{isEN ? 'Resets every Monday' : 'Reset setiap Isnin'}</span>
                  </div>
                  {weeklyLB.entries.length === 0 ? (
                    <div className="lb-empty">{isEN ? 'No games this week yet. Be the first!' : 'Tiada game minggu ini lagi. Jadi yang pertama!'}</div>
                  ) : (
                    <table className="lb-table">
                      <thead>
                        <tr>
                          <th>{t('rank', language)}</th>
                          <th>{isEN ? 'Player' : 'Pemain'}</th>
                          <th>Lv.</th>
                          <th>{t('totalMatchesShort', language)}</th>
                          <th>{t('totalScore', language)}</th>
                          <th>{t('winsCount', language)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyLB.entries.map((entry, index) => (
                          <tr key={index} className={index < 3 ? `lb-top-${index + 1}` : ''}>
                            <td className="lb-rank">{medalEmoji(index)}</td>
                            <td className="lb-team-name">
                              {entry.name}
                              {entry.school && <span className="lb-school-tag">{entry.school}</span>}
                            </td>
                            <td className="lb-level-badge">{entry.level}</td>
                            <td>{entry.matches}</td>
                            <td className="lb-total-score">{entry.total_score}</td>
                            <td className="lb-wins">{entry.wins}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Schools */}
              {activeTab === 'schools' && (
                <div className="lb-table-container" data-testid="leaderboard-schools-table">
                  {schools.length === 0 ? (
                    <div className="lb-empty">{t('noSchools', language)}</div>
                  ) : (
                    <table className="lb-table">
                      <thead><tr><th>{t('rank', language)}</th><th>{t('schoolName', language)}</th><th>{t('totalMatchesShort', language)}</th><th>{t('totalScore', language)}</th><th>{t('totalCorrectShort', language)}</th></tr></thead>
                      <tbody>
                        {schools.map((entry, index) => (
                          <tr key={index} className={index < 3 ? `lb-top-${index + 1}` : ''}>
                            <td className="lb-rank">{medalEmoji(index)}</td>
                            <td className="lb-team-name">{entry.school_name}</td>
                            <td className="lb-wins">{entry.total_matches}</td>
                            <td className="lb-total-score">{entry.total_score}</td>
                            <td>{entry.total_correct}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* All-time Teams */}
              {activeTab === 'leaderboard' && (
                <div className="lb-table-container" data-testid="leaderboard-table">
                  {leaderboard.length === 0 ? (
                    <div className="lb-empty">{t('noLeaderboard', language)}</div>
                  ) : (
                    <table className="lb-table">
                      <thead><tr><th>{t('rank', language)}</th><th>{t('team', language)}</th><th>{t('winsCount', language)}</th><th>{t('totalScore', language)}</th></tr></thead>
                      <tbody>
                        {leaderboard.map((entry, index) => (
                          <tr key={index} className={index < 3 ? `lb-top-${index + 1}` : ''}>
                            <td className="lb-rank">{medalEmoji(index)}</td>
                            <td className="lb-team-name">{entry.team_name}</td>
                            <td className="lb-wins">{entry.wins}</td>
                            <td className="lb-total-score">{entry.total_score}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Recent Matches */}
              {activeTab === 'matches' && (
                <div className="lb-matches-container">
                  {matches.length === 0 ? (
                    <div className="lb-empty">{t('noMatches', language)}</div>
                  ) : (
                    <div className="lb-matches-list">
                      {matches.map((match, index) => (
                        <div key={index} className="lb-match-card">
                          <div className="lb-match-teams">
                            <span className={`lb-match-team ${match.winner === 'team1' ? 'lb-match-winner' : ''}`}>{match.team1_name}</span>
                            <span className="lb-match-score">{match.team1_score} - {match.team2_score}</span>
                            <span className={`lb-match-team ${match.winner === 'team2' ? 'lb-match-winner' : ''}`}>{match.team2_name}</span>
                          </div>
                          <div className="lb-match-meta">
                            <span className="lb-match-difficulty">{match.difficulty}</span>
                            {match.school_name && <span className="lb-match-school">{match.school_name}</span>}
                            <span className="lb-match-date">{formatDate(match.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <button className="setup-back-btn" onClick={() => navigate('/')} data-testid="leaderboard-back-home-button" style={{ marginTop: '16px' }}>
            &larr; {t('backToHome', language)}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
